import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDate, expenseCategories, paymentModes, getCategoryLabel } from '../lib/utils';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  TrendingUp,
  TrendingDown,
  Plus,
  IndianRupee,
  Calendar as CalendarIcon,
  Search,
  Pencil,
  Trash2,
  ArrowLeftRight,
  CheckCircle,
  AlertCircle,
  Link2,
  ArrowUpDown,
  CreditCard,
  Building2,
  Shield,
  ShieldCheck,
  ShieldX,
  Briefcase,
  User,
  Tag
} from 'lucide-react';

export default function Transactions() {
  const { user } = useAuth();
  const canCreate = user?.permissions?.can_create !== false;
  const canEdit = user?.permissions?.can_edit !== false;
  const canDelete = user?.permissions?.can_delete !== false;
  const [revenues, setRevenues] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [accountMap, setAccountMap] = useState({});
  const [transferStats, setTransferStats] = useState({ matched_count: 0, unmatched_count: 0 });
  const [ventures, setVentures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addType, setAddType] = useState('revenue');
  const [searchQuery, setSearchQuery] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [customCategories, setCustomCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkExpenseId, setLinkExpenseId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedTxIds, setSelectedTxIds] = useState([]);
  const [bulkLinkBankOpen, setBulkLinkBankOpen] = useState(false);
  const [bulkLinkBankId, setBulkLinkBankId] = useState('');
  const [editTransferOpen, setEditTransferOpen] = useState(false);
  const [editTransfer, setEditTransfer] = useState(null);
  const [convertCategory, setConvertCategory] = useState('other');
  const [selectedTransferIds, setSelectedTransferIds] = useState([]);
  const allCategories = [
    { value: 'revenue', label: 'Revenue' },
    ...expenseCategories,
    ...customCategories.map(c => ({ value: c.value, label: c.label })),
    { value: 'investment', label: 'Investment' },
    { value: 'loan_emi', label: 'Loan EMI' },
    { value: 'sip', label: 'SIP' },
  ];

  const [filters, setFilters] = useState({
    venture_id: 'all',
    location_id: 'all',
    bank_account_id: 'all',
    start_date: '',
    end_date: '',
    source_type: 'all',
    verification_status: 'all',
    transaction_type_filter: 'all',
    category_filter: 'all'
  });
  const [appliedFilters, setAppliedFilters] = useState({
    venture_id: 'all',
    location_id: 'all',
    bank_account_id: 'all',
    start_date: '',
    end_date: '',
    source_type: 'all',
    verification_status: 'all',
    transaction_type_filter: 'all',
    category_filter: 'all'
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [ccTransactions, setCcTransactions] = useState([]);

  useEffect(() => { fetchData(); }, []); // Only fetch on initial load

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    fetchDataWithFilters(filters);
  };

  const clearFilters = () => {
    const cleared = { venture_id: 'all', location_id: 'all', bank_account_id: 'all', start_date: '', end_date: '', source_type: 'all', verification_status: 'all', transaction_type_filter: 'all', category_filter: 'all' };
    setFilters(cleared);
    setAppliedFilters(cleared);
    fetchDataWithFilters(cleared);
  };

  const fetchDataWithFilters = async (filterValues) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterValues.venture_id && filterValues.venture_id !== 'all') params.append('venture_id', filterValues.venture_id);
      if (filterValues.location_id && filterValues.location_id !== 'all') params.append('location_id', filterValues.location_id);
      if (filterValues.start_date) params.append('start_date', filterValues.start_date);
      if (filterValues.end_date) params.append('end_date', filterValues.end_date);

      const [revenuesRes, expensesRes, venturesRes, locationsRes, catsRes, transfersRes, projectsRes, bankRes, cardsRes] = await Promise.all([
        api.get(`/revenues?${params}`),
        api.get(`/expenses?${params}`),
        api.get('/ventures'),
        api.get('/locations'),
        api.get('/categories/custom').catch(() => ({ data: [] })),
        api.get('/transfers').catch(() => ({ data: { transfers: [] } })),
        api.get('/projects').catch(() => ({ data: [] })),
        api.get('/bank-accounts').catch(() => ({ data: [] })),
        api.get('/credit-cards').catch(() => ({ data: [] }))
      ]);
      setRevenues(revenuesRes.data);
      setExpenses(expensesRes.data);
      setVentures(venturesRes.data);
      setLocations(locationsRes.data);
      setCustomCategories(catsRes.data);
      setTransfers(transfersRes.data.transfers || []);
      setMatchedPairs(transfersRes.data.matched_pairs || []);
      setAccountMap(transfersRes.data.account_map || {});
      setTransferStats({
        matched_count: transfersRes.data.matched_count || 0,
        unmatched_count: transfersRes.data.unmatched_count || 0
      });
      setProjects(projectsRes.data);
      setBankAccounts(bankRes.data);
      
      // Fetch CC transactions from all cards
      const allCcTxs = [];
      for (const card of cardsRes.data) {
        try {
          const txRes = await api.get(`/credit-cards/${card.card_id}/transactions`);
          txRes.data.forEach(tx => {
            allCcTxs.push({ ...tx, card_name: card.name, source: 'credit_card' });
          });
        } catch {}
      }
      setCcTransactions(allCcTxs);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    fetchDataWithFilters(appliedFilters);
  };

  const handleDelete = async (tx) => {
    if (!window.confirm(`Delete this ${tx.type}?`)) return;
    try {
      if (tx.type === 'revenue') {
        await api.delete(`/revenues/${tx.revenue_id}`);
      } else if (tx.type === 'transfer') {
        await api.delete(`/transfers/${tx.transfer_id}`);
      } else {
        await api.delete(`/expenses/${tx.expense_id}`);
      }
      toast.success('Deleted successfully');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleLinkToProject = async () => {
    if (!linkExpenseId || !selectedProjectId) return;
    try {
      await api.post(`/projects/${selectedProjectId}/link-expense/${linkExpenseId}`);
      toast.success('Linked to project');
      setLinkDialogOpen(false);
      setLinkExpenseId(null);
      setSelectedProjectId('');
      fetchData();
    } catch { toast.error('Failed to link'); }
  };

  const handleFlipType = async (tx) => {
    const newType = tx.type === 'revenue' ? 'expense' : 'revenue';
    if (!window.confirm(`Change this from ${tx.type} to ${newType}?`)) return;
    try {
      await api.post(`/transactions/flip-type`, {
        id: tx.revenue_id || tx.expense_id,
        current_type: tx.type
      });
      toast.success(`Changed to ${newType}`);
      fetchData();
    } catch { toast.error('Failed to change type'); }
  };

  const handleEdit = (tx) => {
    if (tx.type === 'transfer') {
      toast.info('Transfers cannot be edited. Delete and re-import if needed.');
      return;
    }
    setEditItem(tx);
    setAddType(tx.type);
    setAddDialogOpen(true);
  };

  const handleRevertToTransfer = async (expense) => {
    if (!expense.converted_from_transfer_id) {
      toast.error('This expense was not converted from a transfer');
      return;
    }
    if (!window.confirm('Revert this expense back to a transfer?')) return;
    try {
      await api.post(`/expenses/${expense.expense_id}/revert-to-transfer`);
      toast.success('Reverted to transfer');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revert');
    }
  };

  const handleQuickRecategorize = async (tx, newCategory) => {
    const txId = tx.expense_id || tx.revenue_id;
    const txType = tx.type === 'cc_expense' ? 'expense' : tx.type;
    if (!txId || !txType) return;
    try {
      await api.patch('/transactions/quick-recategorize', {
        id: txId,
        type: txType,
        category: newCategory
      });
      toast.success(`Changed to ${getCategoryLabel(newCategory)}. Rule saved for future uploads.`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to recategorize');
    }
  };

  const getVentureName = (id) => ventures.find(v => v.venture_id === id)?.name || 'Unknown';
  const getLocationName = (id) => locations.find(l => l.location_id === id)?.name || 'Unknown';
  const getBankName = (id) => {
    if (!id) return '';
    const ba = bankAccounts.find(b => b.bank_account_id === id);
    return ba ? `${ba.bank_name}${ba.label ? ' - ' + ba.label : ''}` : '';
  };

  const allTransactions = [
    ...revenues.map(r => ({ ...r, type: 'revenue', amount: r.amount, source: 'bank' })),
    ...expenses.map(e => ({ ...e, type: 'expense', amount: -e.amount, source: 'bank' })),
    ...transfers.map(t => ({ ...t, type: 'transfer', amount: t.type === 'credit' ? t.amount : -t.amount, source: 'bank' })),
    ...ccTransactions.map(cc => ({ 
      ...cc, 
      type: 'cc_expense', 
      amount: -(cc.total_amount ?? cc.amount ?? 0),
      source: 'credit_card',
      expense_id: cc.transaction_id,
      vendor: cc.description,
      category: cc.category
    }))
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  const filteredTransactions = (activeTab === 'all'
    ? allTransactions
    : activeTab === 'revenue'
      ? revenues.map(r => ({ ...r, type: 'revenue', source: 'bank' }))
      : activeTab === 'expense'
        ? [...expenses.map(e => ({ ...e, type: 'expense', source: 'bank' })), ...ccTransactions.map(cc => ({ ...cc, type: 'cc_expense', source: 'credit_card', expense_id: cc.transaction_id, vendor: cc.description, category: cc.category }))]
        : transfers.map(t => ({ ...t, type: 'transfer', source: 'bank' }))
  ).filter(tx => {
    // Source type filter (Bank vs CC)
    if (appliedFilters.source_type && appliedFilters.source_type !== 'all') {
      if (appliedFilters.source_type === 'bank' && tx.source === 'credit_card') return false;
      if (appliedFilters.source_type === 'credit_card' && tx.source !== 'credit_card') return false;
    }
    // Verification status filter (for CC transactions)
    if (appliedFilters.verification_status && appliedFilters.verification_status !== 'all') {
      if (tx.source === 'credit_card') {
        const status = tx.verification_status || 'unverified';
        if (appliedFilters.verification_status !== status) return false;
      } else if (appliedFilters.verification_status === 'unverified') {
        // Bank transactions don't have verification_status, skip them if filtering for unverified CC
        return false;
      }
    }
    // Transaction type filter (Business vs Personal)
    if (appliedFilters.transaction_type_filter && appliedFilters.transaction_type_filter !== 'all') {
      if (tx.source === 'credit_card') {
        const txType = tx.transaction_type || 'business';
        if (appliedFilters.transaction_type_filter !== txType) return false;
      } else if (appliedFilters.transaction_type_filter === 'personal') {
        // Bank transactions are business by default
        return false;
      }
    }
    // Bank account filter (uses appliedFilters, not filters)
    if (appliedFilters.bank_account_id && appliedFilters.bank_account_id !== 'all') {
      if (appliedFilters.bank_account_id === 'unlinked') {
        if (tx.bank_account_id && tx.bank_account_id !== '') return false;
      } else {
        if (tx.bank_account_id !== appliedFilters.bank_account_id) return false;
      }
    }
    // Category filter
    if (appliedFilters.category_filter && appliedFilters.category_filter !== 'all') {
      // Skip transfers — they don't have categories
      if (tx.type === 'transfer') return false;
      // Revenues default to 'revenue', not 'other'
      const txCat = tx.type === 'revenue' ? (tx.category || 'revenue') : (tx.category || 'other');
      if (txCat !== appliedFilters.category_filter) return false;
    }
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      tx.vendor?.toLowerCase().includes(q) ||
      tx.notes?.toLowerCase().includes(q) ||
      tx.category?.toLowerCase().includes(q) ||
      tx.description?.toLowerCase().includes(q) ||
      getVentureName(tx.venture_id).toLowerCase().includes(q) ||
      getLocationName(tx.location_id).toLowerCase().includes(q)
    );
  });

  // Get unlinked bank transactions
  const unlinkedTransactions = filteredTransactions.filter(tx => 
    tx.source !== 'credit_card' && !tx.bank_account_id && tx.type !== 'transfer'
  );

  const handleBulkLinkBank = async () => {
    if (!bulkLinkBankId || selectedTxIds.length === 0) {
      toast.error('Select transactions and a bank account');
      return;
    }
    try {
      await api.post('/expenses/bulk-link-bank', {
        expense_ids: selectedTxIds,
        bank_account_id: bulkLinkBankId
      });
      toast.success(`Linked ${selectedTxIds.length} transactions to bank account`);
      setSelectedTxIds([]);
      setBulkLinkBankOpen(false);
      setBulkLinkBankId('');
      fetchData();
    } catch (err) {
      toast.error('Failed to link transactions');
    }
  };

  const handleEditTransfer = async () => {
    if (!editTransfer) return;
    try {
      const fromAcc = editTransfer.from_account_id || '';
      const toAcc = editTransfer.to_account_id || '';
      const payload = {
        from_account_id: fromAcc,
        to_account_id: toAcc,
        bank_account_id: editTransfer.bank_account_id || ''
      };
      // If both accounts are set, mark as manually matched
      if (fromAcc && fromAcc !== 'none' && toAcc && toAcc !== 'none') {
        payload.matched_with = 'manual_reconciliation';
      }
      await api.patch(`/transfers/${editTransfer.transfer_id}`, payload);
      toast.success('Transfer updated');
      setEditTransferOpen(false);
      setEditTransfer(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to update transfer');
    }
  };

  const toggleSelectTx = (txId) => {
    if (selectedTxIds.includes(txId)) {
      setSelectedTxIds(selectedTxIds.filter(id => id !== txId));
    } else {
      setSelectedTxIds([...selectedTxIds, txId]);
    }
  };

  const toggleSelectTransfer = (transferId) => {
    if (selectedTransferIds.includes(transferId)) {
      setSelectedTransferIds(selectedTransferIds.filter(id => id !== transferId));
    } else {
      setSelectedTransferIds([...selectedTransferIds, transferId]);
    }
  };

  const handleConvertToExpense = async () => {
    if (!editTransfer) return;
    if (!convertCategory || convertCategory === 'other') {
      toast.error('Please select a category first');
      return;
    }
    try {
      const res = await api.post(`/transfers/${editTransfer.transfer_id}/convert-to-expense`, {
        category: convertCategory,
        vendor: editTransfer.description
      });
      toast.success(res.data?.message || 'Transfer converted to expense');
      setEditTransferOpen(false);
      setEditTransfer(null);
      setConvertCategory('other');
      fetchData();
    } catch (err) {
      console.error('Convert error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to convert transfer';
      toast.error(errorMsg);
    }
  };

  const handleBulkConvertTransfers = async () => {
    if (selectedTransferIds.length === 0) {
      toast.error('Select transfers to convert');
      return;
    }
    try {
      await api.post('/transfers/bulk-convert-to-expense', {
        transfer_ids: selectedTransferIds,
        category: convertCategory || 'other'
      });
      toast.success(`Converted ${selectedTransferIds.length} transfers to expenses`);
      setSelectedTransferIds([]);
      setConvertCategory('other');
      fetchData();
    } catch (err) {
      toast.error('Failed to convert transfers');
    }
  };

  const unmatchedTransfers = transfers.filter(t => !t.matched_with);

  const selectAllUnlinked = () => {
    const ids = unlinkedTransactions.map(tx => tx.expense_id || tx.revenue_id).filter(Boolean);
    setSelectedTxIds(ids);
  };

  const selectAllUnmatchedTransfers = () => {
    const ids = unmatchedTransfers.map(t => t.transfer_id);
    setSelectedTransferIds(ids);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="transactions-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">Transactions</h1>
          <p className="text-zinc-500 mt-1">Track all revenues and expenses</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedTransferIds.length > 0 && (
            <Button onClick={handleBulkConvertTransfers} className="bg-amber-600 hover:bg-amber-700 rounded-xl" data-testid="bulk-convert-transfers-btn">
              <ArrowUpDown className="w-4 h-4 mr-2" />Convert to Expense ({selectedTransferIds.length})
            </Button>
          )}
          {unmatchedTransfers.length > 0 && selectedTransferIds.length === 0 && activeTab === 'transfer' && (
            <Button onClick={selectAllUnmatchedTransfers} variant="outline" size="sm" className="text-cyan-600 border-cyan-200" data-testid="select-unmatched-transfers-btn">
              <ArrowLeftRight className="w-4 h-4 mr-1" />
              Select Unmatched ({unmatchedTransfers.length})
            </Button>
          )}
          {selectedTxIds.length > 0 && (
            <Button onClick={() => setBulkLinkBankOpen(true)} variant="outline" className="rounded-xl" data-testid="bulk-link-bank-btn">
              <Link2 className="w-4 h-4 mr-2" />Link to Bank ({selectedTxIds.length})
            </Button>
          )}
          {unlinkedTransactions.length > 0 && selectedTxIds.length === 0 && (
            <Button onClick={selectAllUnlinked} variant="outline" size="sm" className="text-amber-600 border-amber-200" data-testid="select-unlinked-btn">
              <AlertCircle className="w-4 h-4 mr-1" />
              Select Unlinked ({unlinkedTransactions.length})
            </Button>
          )}
          <Button onClick={() => { setEditItem(null); setAddDialogOpen(true); setAddType('revenue'); }} className="rounded-xl" data-testid="add-transaction-btn" disabled={!canCreate} style={canCreate ? {} : {display:'none'}}>
            <Plus className="w-4 h-4 mr-2" />Add Transaction
          </Button>
        </div>
      </div>

      <Card className="roch-card mb-6">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[180px] max-w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input placeholder="Search vendor, notes, category..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="transactions-search" />
            </div>
            <Select value={filters.source_type} onValueChange={(value) => setFilters({ ...filters, source_type: value })}>
              <SelectTrigger className="w-[140px]" data-testid="filter-source-type">
                <Building2 className="w-3.5 h-3.5 mr-1.5 text-zinc-400" />
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="bank"><span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />Bank</span></SelectItem>
                <SelectItem value="credit_card"><span className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" />Credit Card</span></SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.verification_status} onValueChange={(value) => setFilters({ ...filters, verification_status: value })}>
              <SelectTrigger className="w-[140px]" data-testid="filter-verification">
                <Shield className="w-3.5 h-3.5 mr-1.5 text-zinc-400" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="verified"><span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-green-600" />Verified</span></SelectItem>
                <SelectItem value="unverified"><span className="flex items-center gap-1.5"><ShieldX className="w-3.5 h-3.5 text-amber-600" />Unverified</span></SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.transaction_type_filter} onValueChange={(value) => setFilters({ ...filters, transaction_type_filter: value })}>
              <SelectTrigger className="w-[140px]" data-testid="filter-tx-type">
                <Briefcase className="w-3.5 h-3.5 mr-1.5 text-zinc-400" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="business"><span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-blue-600" />Business</span></SelectItem>
                <SelectItem value="personal"><span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-purple-600" />Personal</span></SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.category_filter} onValueChange={(value) => setFilters({ ...filters, category_filter: value })}>
              <SelectTrigger className="w-[150px]" data-testid="filter-category">
                <Tag className="w-3.5 h-3.5 mr-1.5 text-zinc-400" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="other"><span className="flex items-center gap-1.5 text-amber-600">Uncategorized (Other)</span></SelectItem>
                {allCategories.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.venture_id} onValueChange={(value) => setFilters({ ...filters, venture_id: value, location_id: 'all' })}>
              <SelectTrigger className="w-[150px]" data-testid="filter-venture"><SelectValue placeholder="All Ventures" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ventures</SelectItem>
                {ventures.map((v) => <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.bank_account_id} onValueChange={(value) => setFilters({ ...filters, bank_account_id: value })}>
              <SelectTrigger className="w-[150px]" data-testid="filter-bank-account"><SelectValue placeholder="All Accounts" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                <SelectItem value="unlinked">Unlinked</SelectItem>
                {bankAccounts.map((ba) => <SelectItem key={ba.bank_account_id} value={ba.bank_account_id}>{ba.bank_name}{ba.label ? ` - ${ba.label}` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="w-[130px] h-9" data-testid="filter-start-date" />
              <span className="text-zinc-400">to</span>
              <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="w-[130px] h-9" data-testid="filter-end-date" />
            </div>
            <Button onClick={applyFilters} className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="apply-filter-btn">
              <Search className="w-4 h-4 mr-1.5" />Apply
            </Button>
            {(appliedFilters.venture_id !== 'all' || appliedFilters.bank_account_id !== 'all' || appliedFilters.start_date || appliedFilters.end_date || appliedFilters.source_type !== 'all' || appliedFilters.verification_status !== 'all' || appliedFilters.transaction_type_filter !== 'all' || appliedFilters.category_filter !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">Clear</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="revenue" className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />Revenue</TabsTrigger>
          <TabsTrigger value="expense" className="flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5" />Expenses</TabsTrigger>
          <TabsTrigger value="transfer" className="flex items-center gap-1" data-testid="transfers-tab"><ArrowLeftRight className="w-3.5 h-3.5" />Transfers{transfers.length > 0 && <span className="ml-1 text-xs bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded-full">{transfers.length}</span>}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Money Flow View - shown when Transfers tab is active */}
      {activeTab === 'transfer' && (matchedPairs.length > 0 || transferStats.unmatched_count > 0) && (
        <div className="space-y-4 mb-6">
          {/* Transfer Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="roch-card">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center">
                    <ArrowLeftRight className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Total Transfers</p>
                    <p className="text-lg font-semibold text-zinc-900">{transfers.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="roch-card">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Reconciled</p>
                    <p className="text-lg font-semibold text-green-700">{transferStats.matched_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="roch-card">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Unmatched</p>
                    <p className="text-lg font-semibold text-amber-700">{transferStats.unmatched_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Matched Pairs - Money Flow */}
          {matchedPairs.length > 0 && (
            <Card className="roch-card" data-testid="money-flow-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <ArrowLeftRight className="w-4 h-4 text-cyan-600" />
                  Money Flow Between Accounts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {matchedPairs.map((pair, i) => {
                  const fromAcc = accountMap[pair.from_account];
                  const toAcc = accountMap[pair.to_account];
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl border border-zinc-100" data-testid={`flow-pair-${i}`}>
                      <div className="flex-1 text-right">
                        <p className="text-sm font-medium text-zinc-800">
                          {fromAcc ? `${fromAcc.bank_name} (****${fromAcc.last4})` : 'Account'}
                        </p>
                        <p className="text-xs text-zinc-400">{fromAcc?.account_type || 'Unknown'}</p>
                      </div>
                      <div className="flex flex-col items-center gap-0.5 px-3">
                        <div className="flex items-center gap-1.5 bg-cyan-50 border border-cyan-200 rounded-full px-3 py-1">
                          <span className="text-sm font-semibold text-cyan-700">{formatCurrency(pair.amount)}</span>
                          <ArrowLeftRight className="w-3.5 h-3.5 text-cyan-500" />
                        </div>
                        <span className="text-[10px] text-zinc-400">{formatDate(pair.date)}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-zinc-800">
                          {toAcc ? `${toAcc.bank_name} (****${toAcc.last4})` : 'Account'}
                        </p>
                        <p className="text-xs text-zinc-400">{toAcc?.account_type || 'Unknown'}</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Card className="roch-card">
        <CardContent className="p-0">
          {filteredTransactions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><CalendarIcon className="w-6 h-6" /></div>
              <h3 className="font-medium text-zinc-900 mb-1">No transactions found</h3>
              <p className="text-sm text-zinc-500">Start by adding your first transaction</p>
            </div>
          ) : (
            <div className="roch-table-wrap">
            <table className="roch-table">
              <thead>
                <tr>
                  <th>Date</th><th>Type</th><th>Source</th><th>Description</th><th>Bank/Card</th><th>Venture</th><th className="text-right">Amount</th><th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx) => (
                  <tr key={tx.revenue_id || tx.expense_id || tx.transfer_id || tx.transaction_id}>
                    <td className="text-zinc-500">{formatDate(tx.date)}</td>
                    <td><span className={`badge ${tx.type === 'revenue' ? 'badge-success' : tx.type === 'transfer' ? 'badge-info' : tx.type === 'cc_expense' ? 'badge-warning' : 'badge-error'}`}>{tx.type === 'revenue' ? 'Revenue' : tx.type === 'transfer' ? 'Transfer' : tx.type === 'cc_expense' ? 'CC Expense' : 'Expense'}</span></td>
                    <td>
                      {tx.source === 'credit_card' ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200">
                          <CreditCard className="w-3 h-3" />CC
                          {tx.verification_status === 'verified' && <ShieldCheck className="w-3 h-3 text-green-600" />}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                          <Building2 className="w-3 h-3" />Bank
                        </span>
                      )}
                    </td>
                    <td>{tx.type === 'transfer' ? (
                      <div>
                        <span className="font-medium text-cyan-700">{tx.description}</span>
                        {tx.matched_with ? <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">reconciled</span> : <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">unmatched</span>}
                      </div>
                    ) : tx.type === 'expense' || tx.type === 'cc_expense' ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium">{tx.vendor || tx.description}</span>
                        <Select value={tx.category || 'other'} onValueChange={(val) => handleQuickRecategorize(tx, val)}>
                          <SelectTrigger className={`h-6 text-[11px] px-2 py-0 border rounded-full w-auto inline-flex gap-1 ${tx.category === 'other' || !tx.category ? 'bg-amber-50 text-amber-700 border-amber-300 animate-pulse' : 'bg-zinc-100 text-zinc-600 border-zinc-200'}`} data-testid={`recat-${tx.expense_id || tx.transaction_id}`}>
                            <Tag className="w-3 h-3" />
                            <span>{getCategoryLabel(tx.category)}</span>
                          </SelectTrigger>
                          <SelectContent>
                            {allCategories.map(c => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {tx.project_id && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200">{projects.find(p => p.project_id === tx.project_id)?.name || 'Project'}</span>}
                        {tx.type === 'cc_expense' && tx.transaction_type && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tx.transaction_type === 'business' ? 'bg-blue-50 text-blue-600 border-blue-200' : tx.transaction_type === 'personal' ? 'bg-purple-50 text-purple-600 border-purple-200' : 'bg-amber-50 text-amber-600 border-amber-200'} border`}>{tx.transaction_type}</span>}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-zinc-500">{tx.notes || tx.source || 'Daily Sales'}</span>
                        <Select value={tx.category || 'revenue'} onValueChange={(val) => handleQuickRecategorize(tx, val)}>
                          <SelectTrigger className={`h-6 text-[11px] px-2 py-0 border rounded-full w-auto inline-flex gap-1 ${tx.category === 'other' || !tx.category ? 'bg-amber-50 text-amber-700 border-amber-300 animate-pulse' : 'bg-zinc-100 text-zinc-600 border-zinc-200'}`} data-testid={`recat-${tx.revenue_id}`}>
                            <Tag className="w-3 h-3" />
                            <span>{getCategoryLabel(tx.category || 'revenue')}</span>
                          </SelectTrigger>
                          <SelectContent>
                            {allCategories.map(c => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}</td>
                    <td className="text-zinc-500 text-xs">{tx.source === 'credit_card' ? (tx.card_name || 'Credit Card') : (getBankName(tx.bank_account_id) || <span className="text-zinc-300">-</span>)}</td>
                    <td className="text-zinc-600">{tx.venture_id ? getVentureName(tx.venture_id) : '-'}</td>
                    <td className={`text-right font-medium ${tx.type === 'revenue' ? 'text-green-600' : tx.type === 'transfer' ? 'text-cyan-600' : tx.type === 'cc_expense' ? 'text-purple-600' : 'text-red-600'}`}>{tx.type === 'revenue' || (tx.type === 'transfer' && tx.amount > 0) ? '+' : '-'}{formatCurrency(Math.abs(tx.amount || tx.total_amount || 0))}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        {tx.type === 'transfer' && !tx.matched_with && (
                          <>
                            <input 
                              type="checkbox" 
                              checked={selectedTransferIds.includes(tx.transfer_id)}
                              onChange={() => toggleSelectTransfer(tx.transfer_id)}
                              className="w-4 h-4 rounded border-zinc-300 text-cyan-600 focus:ring-cyan-500 mr-1"
                              title="Select for bulk convert"
                            />
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-cyan-500" title="Edit Transfer"
                              onClick={() => { setEditTransfer(tx); setEditTransferOpen(true); setConvertCategory('other'); }}
                              data-testid={`edit-transfer-${tx.transfer_id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        {(tx.type === 'revenue' || tx.type === 'expense') && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-500" title={`Change to ${tx.type === 'revenue' ? 'Expense' : 'Revenue'}`}
                            onClick={() => handleFlipType(tx)}
                            data-testid={`flip-type-${tx.revenue_id || tx.expense_id}`}>
                            <ArrowUpDown className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {tx.type === 'expense' && !tx.project_id && projects.length > 0 && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-purple-500" title="Link to Project"
                            onClick={() => { setLinkExpenseId(tx.expense_id); setLinkDialogOpen(true); }}
                            data-testid={`link-project-${tx.expense_id}`}>
                            <Link2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {(tx.type === 'expense' || tx.type === 'revenue') && !tx.bank_account_id && (
                          <input 
                            type="checkbox" 
                            checked={selectedTxIds.includes(tx.expense_id || tx.revenue_id)}
                            onChange={() => toggleSelectTx(tx.expense_id || tx.revenue_id)}
                            className="w-4 h-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                            title="Select for bulk action"
                          />
                        )}
                        {canEdit && tx.type !== 'cc_expense' && tx.type !== 'transfer' && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(tx)} data-testid={`edit-tx-${tx.revenue_id || tx.expense_id}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                        {tx.type === 'expense' && tx.converted_from_transfer_id && <Button variant="ghost" size="icon" className="h-7 w-7 text-cyan-500 hover:text-cyan-700" onClick={() => handleRevertToTransfer(tx)} title="Revert to transfer" data-testid={`revert-tx-${tx.expense_id}`}><ArrowLeftRight className="w-3.5 h-3.5" /></Button>}
                        {canDelete && tx.type !== 'cc_expense' && tx.type !== 'transfer' && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(tx)} data-testid={`delete-tx-${tx.revenue_id || tx.expense_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddTransactionDialog
        open={addDialogOpen}
        onOpenChange={(v) => { setAddDialogOpen(v); if (!v) setEditItem(null); }}
        type={addType}
        setType={setAddType}
        ventures={ventures}
        locations={locations}
        onSuccess={fetchData}
        editItem={editItem}
        allCategories={allCategories}
      />

      {/* Link to Project Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-[360px]" data-testid="link-project-dialog">
          <DialogHeader>
            <DialogTitle>Link to Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Project</Label>
              <Select value={selectedProjectId || 'none'} onValueChange={(v) => setSelectedProjectId(v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1.5" data-testid="link-project-select">
                  <SelectValue placeholder="Choose a project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select...</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleLinkToProject} disabled={!selectedProjectId} className="w-full rounded-xl" data-testid="confirm-link-btn">
              <Link2 className="w-4 h-4 mr-2" />Link to Project
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Link Bank Account Dialog */}
      <Dialog open={bulkLinkBankOpen} onOpenChange={setBulkLinkBankOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="bulk-link-bank-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Link Transactions to Bank Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-zinc-600">
              Link <span className="font-semibold text-zinc-900">{selectedTxIds.length}</span> selected transactions to a bank account.
            </p>
            <div>
              <Label>Select Bank Account</Label>
              <Select value={bulkLinkBankId || 'none'} onValueChange={(v) => setBulkLinkBankId(v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1.5" data-testid="bulk-link-bank-select">
                  <SelectValue placeholder="Choose bank account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select...</SelectItem>
                  {bankAccounts.map((ba) => (
                    <SelectItem key={ba.bank_account_id} value={ba.bank_account_id}>
                      {ba.bank_name} {ba.label ? `(${ba.label})` : ''} ****{ba.account_number_masked?.slice(-4) || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setBulkLinkBankOpen(false); setSelectedTxIds([]); }} className="flex-1">Cancel</Button>
              <Button onClick={handleBulkLinkBank} disabled={!bulkLinkBankId} className="flex-1" data-testid="confirm-bulk-link-btn">
                <Link2 className="w-4 h-4 mr-2" />Link All
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Transfer Dialog */}
      <Dialog open={editTransferOpen} onOpenChange={setEditTransferOpen}>
        <DialogContent className="sm:max-w-[450px]" data-testid="edit-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-cyan-600" />
              Edit Transfer
            </DialogTitle>
          </DialogHeader>
          {editTransfer && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-zinc-50 rounded-lg">
                <p className="text-sm font-medium">{editTransfer.description}</p>
                <p className="text-xs text-zinc-500 mt-1">{formatDate(editTransfer.date)} • {formatCurrency(editTransfer.amount)}</p>
              </div>
              <div>
                <Label>From Account</Label>
                <Select value={editTransfer.from_account_id || 'none'} onValueChange={(v) => setEditTransfer({ ...editTransfer, from_account_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="transfer-from-account">
                    <SelectValue placeholder="Select source account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {bankAccounts.map((ba) => (
                      <SelectItem key={ba.bank_account_id} value={ba.bank_account_id}>
                        {ba.bank_name} {ba.label ? `(${ba.label})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To Account</Label>
                <Select value={editTransfer.to_account_id || 'none'} onValueChange={(v) => setEditTransfer({ ...editTransfer, to_account_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="transfer-to-account">
                    <SelectValue placeholder="Select destination account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {bankAccounts.map((ba) => (
                      <SelectItem key={ba.bank_account_id} value={ba.bank_account_id}>
                        {ba.bank_name} {ba.label ? `(${ba.label})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { setEditTransferOpen(false); setEditTransfer(null); }} className="flex-1">Cancel</Button>
                <Button onClick={handleEditTransfer} className="flex-1" data-testid="save-transfer-btn">Save Changes</Button>
              </div>
              
              {/* Convert to Expense Section */}
              <div className="mt-4 pt-4 border-t border-zinc-200">
                <p className="text-sm font-medium text-zinc-700 mb-2">Or convert this transfer to an expense:</p>
                <div className="flex gap-2">
                  <Select value={convertCategory} onValueChange={setConvertCategory}>
                    <SelectTrigger className="flex-1" data-testid="convert-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {allCategories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleConvertToExpense} variant="destructive" className="whitespace-nowrap" data-testid="convert-to-expense-btn">
                    <ArrowUpDown className="w-4 h-4 mr-2" />Convert to Expense
                  </Button>
                </div>
                <p className="text-xs text-zinc-400 mt-2">This will delete the transfer and create an expense with the same amount.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddTransactionDialog({ open, onOpenChange, type, setType, ventures, locations, onSuccess, editItem, allCategories }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '', vendor: '', category: '',
    venture_id: '', location_id: '', payment_mode: 'upi', notes: ''
  });

  useEffect(() => {
    if (editItem) {
      setForm({
        date: editItem.date || '',
        amount: String(Math.abs(editItem.amount) || ''),
        vendor: editItem.vendor || '',
        source: editItem.source || editItem.notes?.replace('[Bank Import] ', '') || '',
        category: editItem.category || '',
        venture_id: editItem.venture_id || '',
        location_id: editItem.location_id || '',
        payment_mode: editItem.payment_mode || 'upi',
        notes: editItem.notes || ''
      });
    } else {
      setForm({ date: new Date().toISOString().split('T')[0], amount: '', vendor: '', category: '', venture_id: '', location_id: '', payment_mode: 'upi', notes: '' });
    }
  }, [editItem, open]);

  const filteredLocations = locations.filter(l => l.venture_id === form.venture_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.venture_id || !form.amount) { toast.error('Please fill all required fields'); return; }
    setLoading(true);
    try {
      if (editItem) {
        if (type === 'revenue') {
          await api.put(`/revenues/${editItem.revenue_id}`, { date: form.date, venture_id: form.venture_id, location_id: form.location_id, amount: parseFloat(form.amount), source: form.source || '', category: form.category || '', notes: form.notes });
        } else {
          await api.put(`/expenses/${editItem.expense_id}`, { ...form, amount: parseFloat(form.amount), tax_category: null });
        }
        toast.success('Updated successfully');
      } else {
        if (type === 'revenue') {
          await api.post('/revenues', { date: form.date, venture_id: form.venture_id, location_id: form.location_id, amount: parseFloat(form.amount), source: form.source || '', category: form.category || 'revenue', notes: form.notes });
        } else {
          await api.post('/expenses', { ...form, amount: parseFloat(form.amount), tax_category: null });
        }
        toast.success(`${type === 'revenue' ? 'Revenue' : 'Expense'} recorded`);
      }
      onOpenChange(false);
      onSuccess();
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader><DialogTitle>{editItem ? 'Edit' : 'Add'} Transaction</DialogTitle></DialogHeader>
        {!editItem && (
          <Tabs value={type} onValueChange={setType} className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="revenue">Revenue</TabsTrigger>
              <TabsTrigger value="expense">Expense</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Amount</Label><div className="relative mt-1.5"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" placeholder="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="pl-9" /></div></div>
          </div>
          {type === 'expense' && (
            <>
              <div><Label>Vendor</Label><Input placeholder="Vendor name" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select category" /></SelectTrigger><SelectContent>{allCategories.map((cat) => <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label>Payment Mode</Label>
                  <Select value={form.payment_mode} onValueChange={(value) => setForm({ ...form, payment_mode: value })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select mode" /></SelectTrigger><SelectContent>{paymentModes.map((mode) => <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
            </>
          )}
          {type === 'revenue' && (
            <>
              <div><Label>Source / Description</Label><Input placeholder="Revenue source (e.g. Swiggy, Dine-in)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1.5" data-testid="revenue-source-input" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}><SelectTrigger className="mt-1.5" data-testid="revenue-category-select"><SelectValue placeholder="Select category" /></SelectTrigger><SelectContent>
                  {allCategories.map((cat) => <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>)}
                </SelectContent></Select>
              </div>
            </>
          )}
          <div><Label>Venture</Label>
            <Select value={form.venture_id} onValueChange={(value) => setForm({ ...form, venture_id: value, location_id: '' })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select venture" /></SelectTrigger><SelectContent>{ventures.map((v) => <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Location</Label>
            <Select value={form.location_id} onValueChange={(value) => setForm({ ...form, location_id: value })} disabled={!form.venture_id}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select location" /></SelectTrigger><SelectContent>{filteredLocations.map((loc) => <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Notes</Label><Input placeholder="Any additional notes..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Saving...' : editItem ? 'Update' : `Record ${type === 'revenue' ? 'Revenue' : 'Expense'}`}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
